import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './LandingPage.css';

import heroBackdrop from '../assets/页面背景.jpg';
import backdrop01 from '../assets/介绍背景1.png';
import backdrop02 from '../assets/介绍背景2.png';
import backdrop03 from '../assets/介绍背景3.png';
import backdrop04 from '../assets/介绍背景4.jpg';
import featureChat from '../assets/介绍图集/ai中医问诊.png';
import featureCalendar from '../assets/介绍图集/养生日程.png';
import featureImaging from '../assets/介绍图集/影像采集中心.png';
import featureProfile from '../assets/介绍图集/用户资料.png';
import featureCommunity from '../assets/介绍图集/社区.png';
import featureHerbs from '../assets/介绍图集/药材库.png';
import featureRecipe from '../assets/介绍图集/药膳方.png';

const backdrops = [backdrop01, backdrop02, backdrop03, backdrop04];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement | null>(null);

  const featureBlocks = useMemo(() => ([
    {
      title: 'AI 望闻问切 · 智能问诊中枢',
      description: '结合 LangChain 与通义千问大模型，联动知识图谱、病例档案与药材库，实时生成可信赖的中医问诊方案。',
      image: featureChat,
    },
    {
      title: '个性化药膳 · 动态调理方案',
      description: '依据体质画像与生活习惯推荐药膳、茶饮与日常起居建议，闭环管理全家人的养生节奏。',
      image: featureRecipe,
    },
    {
      title: '多模态 AI · 影像辨证',
      description: '面诊影像融合舌象、面色、肤理等特征，MindSpore 轻量模型快速推断九大体质倾向。',
      image: featureImaging,
    },
  ]), []);

  const highlightCards = useMemo(() => ([
    {
      title: '养生日历',
      description: '一键同步节气提醒、草本养护日程与体质波动趋势，让好状态成为日常。',
      image: featureCalendar,
    },
    {
      title: '健康画像',
      description: '动态汇总问诊摘要、检验结果与家族史，生成可视化健康画像与风险提示。',
      image: featureProfile,
    },
    {
      title: '社区共创',
      description: '沉浸式社区围绕中医调养话题，沉淀专家问答、症状心得与每日练功打卡。',
      image: featureCommunity,
    },
    {
      title: '东方本草',
      description: '跨页本草图鉴搭配药性图谱，AI 推荐炮制工艺、配伍禁忌与现代药理。',
      image: featureHerbs,
    },
  ]), []);

  const techStacks = useMemo(() => ([
    {
      label: '架构核心',
      detail: 'Next.js + React 19 + TypeScript 构建沉浸式界面，结合 Lenis 平滑滚动与 GSAP 动态视效。',
    },
    {
      label: '智能引擎',
      detail: 'LangChain 智能调度通义千问 LLM，辅以 Chroma 向量检索完成中医语境下的 RAG 知识增强。',
    },
    {
      label: 'AI 体质建模',
      detail: 'MindSpore 轻量级分类器推断九大体质，并驱动个性化调理建议与药膳生成链路。',
    },
    {
      label: '数据安全',
      detail: 'FastAPI 服务端通过令牌权限与审计追踪构建安全闭环，支持本地化部署与私有化扩展。',
    },
  ]), []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Hero Title Animation
    gsap.to('.landing-hero__title', {
      opacity: 1,
      y: 0,
      duration: 1.5,
      ease: 'power3.out',
      delay: 0.5,
    });

    // Panel Animations
    const panels = gsap.utils.toArray('.lp-panel');
    panels.forEach((panel: any, i) => {
      gsap.fromTo(
        panel.querySelectorAll('.lp-panel__order, .lp-panel__title, .lp-panel__description'),
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.2,
          scrollTrigger: {
            trigger: panel,
            start: 'top 70%',
            end: 'bottom top',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    // Final CTA Button
    gsap.from('.lp-section--cta', {
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: '.lp-section--cta',
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  }, []);

  return (
    <div className="landing">
      <section
        className="landing-hero"
        style={{ backgroundImage: `url(${heroBackdrop})` }}
      >
        <div className="landing-hero__content">
          <h1 className="landing-hero__title" data-text="中医新智">
            中医新智
          </h1>
        </div>
      </section>

      <section className="lp-section lp-section--panels">
        {backdrops.map((background, index) => (
          <article className="lp-panel" key={background} style={{ backgroundImage: `url(${background})` }}>
            <div className="lp-panel__overlay" />
            <div className="lp-panel__body">
              <span className="lp-panel__order">0{index + 1}</span>
              <h2 className="lp-panel__title">
                {['智能问诊编排', '数字体质画像', '知识触达引擎', '沉浸式体验'][index]}
              </h2>
              <p className="lp-panel__description">
                {
                  [
                    '融合 RAG 技术实时调度经典医案与临床指南，让问诊建议可追溯、可解释。',
                    'MindSpore 模型捕捉睡眠、情绪与饮食信号，动态评估体质与亚健康趋势。',
                    'Chroma 向量检索将海量知识文档转化为语义节点，实现毫秒级信息召回。',
                    'Lenis 与 GSAP 打造的沉浸式交互，将中医之美延展为可感知的数字旅程。',
                  ][index]
                }
              </p>
            </div>
          </article>
        ))}
      </section>

      <section className="lp-section lp-section--features">
        <div className="lp-section__header">
          <span className="lp-eyebrow">核心能力矩阵</span>
          <h2>全链路健康调养，贯穿诊前诊中诊后</h2>
        </div>
        <div className="lp-feature-grid">
          {featureBlocks.map((feature) => (
            <article className="lp-feature-card" key={feature.title}>
              <img src={feature.image} alt={feature.title} className="lp-feature-card__image" />
              <div className="lp-feature-card__body">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section--highlights">
        <div className="lp-section__header">
          <span className="lp-eyebrow">体验亮点</span>
          <h2>在细节中，遇见人机共创的养生日常</h2>
        </div>
        <div className="lp-highlight-marquee">
          {highlightCards.map((card) => (
            <article className="lp-highlight-card" key={card.title}>
              <div className="lp-highlight-card__image-wrapper">
                <img src={card.image} alt={card.title} />
              </div>
              <div className="lp-highlight-card__content">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section--tech">
        <div className="lp-section__header">
          <span className="lp-eyebrow">技术底座</span>
          <h2>云端到终端的多模协同，确保稳态与可扩展性</h2>
        </div>
        <div className="lp-tech-grid">
          {techStacks.map((stack) => (
            <article className="lp-tech-card" key={stack.label}>
              <span className="lp-tech-card__label">{stack.label}</span>
              <p>{stack.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section--cta">
        <div className="lp-cta">
          <div className="lp-cta__copy">
            <span className="lp-eyebrow">立即出发</span>
            <h2>与「中医新智」一起，让健康管理真正以你为中心</h2>
            <p>登录后即可体验全套 AI 问诊、药膳生成、社区协作与健康档案管理能力。</p>
          </div>
          <button type="button" className="landing-cta-button landing-cta-button--ghost" onClick={() => navigate('/login')}>
            立即体验
            <span aria-hidden>→</span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
